import classNames from "classnames";
import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import Slider, { SliderTheme } from "./Slider/Slider";

interface ISliderInputProps {
  initialInputValue: number;
  label: string;
  handleValueChange: (value: number) => void;
  /**
   * Theme of the component;
   * defaults to a light theme.
   */
  theme?: SliderTheme;
  unit?: string;
  /**
   * The lowest possible number in the slider;
   * defaults to 0 if not provided.
   */
  minimum?: number;
  /**
   * The highest possible number in the slider;
   * defaults to 100 if not provided.
   */
  maximum?: number;
}
/*
 * This component has a range and text input to control the same input value
 * handleValueChange will get called from number 0-100
 */
@observer
class SliderInput extends React.Component<ISliderInputProps> {
  public static defaultProps = {
    initialInputValue: 25,
    theme: SliderTheme.Light,
    minimum: 0,
    handleValueChange: () => {},
  };
  @observable private rangeInputValue: number;
  @observable private textInputValue: string;

  constructor(props: ISliderInputProps) {
    super(props);
    this.rangeInputValue = this.props.initialInputValue;
    this.textInputValue = this.props.initialInputValue.toString();
  }

  public render() {
    const { label, theme, unit, minimum } = this.props;

    return (
      <div
        className={classNames({
          "slider-input": true,
          "slider-input--dark": theme === SliderTheme.Dark,
        })}
      >
        <div className="slider-input__input-container">
          <label className="slider-input__label">
            {label}
            <span className="slider-input__unit">{unit}</span>
          </label>
          <div>
            <input
              className="slider-input__text-input"
              type="text"
              onChange={this.onTextInputChange}
              onBlur={this.onTextInputBlur}
              onKeyPress={this.onKeyPressed}
              value={this.textInputValue}
            />
          </div>
        </div>

        <div className="slider-input__input-container">
          <Slider
            value={this.rangeInputValue}
            minimum={minimum}
            maximum={this.maximum}
            theme={theme}
            onChange={this.onSliderChange}
          />
        </div>
      </div>
    );
  }

  private onSliderChange = (newValue: number) => {
    this.rangeInputValue = newValue;
    this.textInputValue = newValue.toString();
    this.props.handleValueChange(newValue);
  };

  private onTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = Number(event.target.value);

    if (isNaN(inputValue)) {
      return;
    }

    const { minimum } = this.props;

    const validInputValue = this.getValidInputValue(inputValue, minimum);

    if (event.target.value.endsWith(".")) {
      // Allow the text input to end with a dot but only if it's on the way to adding another
      // number
      this.textInputValue = event.target.value;
      this.rangeInputValue = validInputValue;
    } else {
      this.textInputValue = inputValue.toString();
      this.rangeInputValue = validInputValue;
    }
    this.props.handleValueChange(validInputValue);
  };

  private onTextInputBlur = () => {
    if (this.props.minimum != undefined && this.isBelowMin(Number(this.textInputValue))) {
      this.textInputValue = this.props.minimum.toString();
    }
  };

  private isBelowMin(inputValue: number) {
    return this.props.minimum != undefined && this.props.minimum > inputValue;
  }

  private isAboveMax(inputValue: number) {
    return inputValue > this.maximum;
  }

  private onKeyPressed = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      (event.target as HTMLInputElement).blur();
    }
  };

  private getValidInputValue = (inputValue: number, minimum?: number): number => {
    if (minimum != undefined && this.isBelowMin(inputValue)) {
      return minimum;
    } else if (this.isAboveMax(inputValue)) {
      return this.maximum;
    } else {
      return inputValue;
    }
  };

  @computed
  private get maximum() {
    return this.props.maximum ?? 100;
  }
}

export default SliderInput;
